import type { DailyRow, Mall, PlAdsResponse } from "../../../types/pl";
import { StackedBarChart } from "../../../components/StackedBarChart";
import { BasisBadge } from "../../../components/BasisBadge";
import { HeatCell } from "../../../components/Heatmap";
import { man, pct, roasFmt, yen } from "../../../lib/plFormat";

const MALL_LABEL: Record<Mall, string> = { shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon" };
const MALL_COLOR: Record<Mall, string> = { shopify: "#5a8f4e", rakuten: "#c43d3a", amazon: "#e0922a" };

interface AdsTabProps {
  data: PlAdsResponse;
  chartDaily: DailyRow[];
  chartFrom: string;
  chartTo: string;
  mall: Mall | "all";
}

export function AdsTab({ data, chartDaily, chartFrom, chartTo, mall }: AdsTabProps) {
  const { kpis, channels, matrix } = data;
  const maxSpend = Math.max(1, ...channels.map((c) => c.spend.value));

  const kpiList = [
    { label: "広告費", value: yen(kpis.ad_spend.value) },
    { label: "広告費率（売上比）", value: pct(kpis.ad_ratio) },
    { label: "ROAS（広告経由売上 ÷ 広告費）", value: roasFmt(kpis.roas_attributed) },
    { label: "全体ROAS（売上 ÷ 広告費）", value: roasFmt(kpis.roas_total) },
  ];

  const sortedChannels = [...channels].sort((a, b) => b.spend.value - a.spend.value);
  const maxCell = Math.max(1, ...matrix.flatMap((r) => Object.values(r.cells)));
  const channelKeys = channels.map((c) => c.key);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="pl-kpi-grid">
        {kpiList.map((k) => (
          <div className="pl-kpi-card" key={k.label}>
            <span className="pl-kpi-label">{k.label}</span>
            <span className="pl-kpi-value">{k.value}</span>
          </div>
        ))}
      </div>

      <div className="pl-two-col">
        <div className="pl-card">
          <span className="pl-card-title">チャネル別 広告費</span>
          <div className="pl-table-wrap">
            <div style={{ minWidth: 560 }}>
              <div className="pl-ch-head">
                <span>チャネル</span>
                <span>広告費</span>
                <span style={{ textAlign: "right" }}>広告経由売上</span>
                <span style={{ textAlign: "right" }}>売上比</span>
                <span style={{ textAlign: "right" }}>ROAS</span>
              </div>
              {sortedChannels.map((c) => (
                <div className="pl-ch-row" key={c.key}>
                  <span className="pl-ch-name">
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: MALL_COLOR[c.mall], flexShrink: 0 }} />
                    <span className="pl-ch-name-text">
                      <span>{c.label}</span>
                      <span className="pl-ch-mall">{MALL_LABEL[c.mall]}</span>
                    </span>
                  </span>
                  {c.status === "not_connected" ? (
                    <span className="pl-ch-not-connected">未連携</span>
                  ) : (
                    <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span>
                        {yen(c.spend.value)} <BasisBadge basis={c.spend.basis} note={c.spend.note} />
                      </span>
                      <span className="pl-ch-spend-bar-track">
                        <span className="pl-ch-spend-bar" style={{ width: `${(c.spend.value / maxSpend) * 100}%`, background: MALL_COLOR[c.mall] }} />
                      </span>
                    </span>
                  )}
                  <span style={{ textAlign: "right" }}>{c.ad_sales ? man(c.ad_sales.value) : "—"}</span>
                  <span style={{ textAlign: "right" }}>{pct(c.ratio)}</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{roasFmt(c.roas)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="pl-card">
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="pl-card-title">日別 広告費</span>
          </div>
          <StackedBarChart daily={chartDaily} from={chartFrom} to={chartTo} metric="ad_spend" mallFilter={mall === "all" ? null : mall} height={200} />
        </div>
      </div>

      <div className="pl-card">
        <div className="pl-card-head">
          <span className="pl-card-title">商品 × チャネル 広告費</span>
          <span className="pl-card-sub">濃いほど広告費が大きい ・ 右端は商品別ROAS（広告経由売上 ÷ 広告費）</span>
        </div>
        {matrix.length === 0 ? (
          <div className="pl-empty">対象期間・モールにデータがありません。</div>
        ) : (
          <div className="pl-table-wrap">
            <div style={{ minWidth: 900, display: "flex", flexDirection: "column", fontSize: 12 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `minmax(180px,1.8fr) repeat(${channelKeys.length},minmax(72px,1fr)) 96px 60px`,
                  gap: 4,
                  padding: "6px 0",
                  color: "var(--pl-muted)",
                  borderBottom: "1px solid var(--pl-border-card)",
                }}
              >
                <span>商品</span>
                {channels.map((c) => (
                  <span key={c.key} style={{ textAlign: "right" }}>
                    {c.label.replace("Amazon ", "")}
                  </span>
                ))}
                <span style={{ textAlign: "right" }}>合計</span>
                <span style={{ textAlign: "right" }}>ROAS</span>
              </div>
              {matrix.map((r) => (
                <div
                  key={r.sku}
                  style={{
                    display: "grid",
                    gridTemplateColumns: `minmax(180px,1.8fr) repeat(${channelKeys.length},minmax(72px,1fr)) 96px 60px`,
                    gap: 4,
                    padding: "3px 0",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 13 }}>{r.name}</span>
                  {channelKeys.map((k) => (
                    <HeatCell key={k} value={r.cells[k] ?? 0} max={maxCell} text={man(r.cells[k] ?? 0)} />
                  ))}
                  <span style={{ textAlign: "right", fontWeight: 600, fontSize: 13 }}>{yen(r.total)}</span>
                  <span style={{ textAlign: "right", fontSize: 13 }}>{roasFmt(r.roas)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
